import React from 'react';
import {useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {Checkbox} from '@edna/components';
import {Stack} from '@edna/components/primitives';

import {CheckboxGroup, FilterField, FiltersSection, FlatCheckboxGroup} from 'src/components';

import {
  useGetMessageMatcherPresetIndustriesQuery,
  useGetMessageMatcherPresetTopicsQuery,
} from './api';
import {TFilters} from './definitions';

export const FiltersContent = React.memo(() => {
  const {t} = useTranslation();

  const {setValue, getValues, watch} = useFormContext<TFilters>();

  const {data: topics = []} = useGetMessageMatcherPresetTopicsQuery();
  const {data: industries = []} = useGetMessageMatcherPresetIndustriesQuery();

  const topicsFilter = watch('topics');
  const industriesFilter = watch('industries');

  const handleFilter = React.useCallback(
    (filterName: 'topics' | 'industries', name: string) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        const values = getValues(filterName);

        setValue(
          filterName,
          checked ? [...values, name] : values.filter((value) => value !== name),
        );
      },
    [setValue],
  );

  return (
    <Stack size="6" isFullWidth>
      <FilterField label={t('MessageMatcherPresets:filters.industry')}>
        <FlatCheckboxGroup>
          {industries.map((industry, index) => (
            <Checkbox
              key={index}
              type="checkbox"
              checked={industriesFilter.includes(industry)}
              onChange={handleFilter('industries', industry)}
            >
              {t(`MessageMatcherPresets:industry.${industry}`, industry)}
            </Checkbox>
          ))}
        </FlatCheckboxGroup>
      </FilterField>
      <FiltersSection>
        <FilterField label={t('MessageMatcherPresets:filters.topic')}>
          <CheckboxGroup columns={2}>
            {topics.map((topic, index) => (
              <Checkbox
                key={index}
                type="checkbox"
                checked={topicsFilter.includes(topic)}
                onChange={handleFilter('topics', topic)}
              >
                {t(`MessageMatcherPresets:topic.${topic}`, topic)}
              </Checkbox>
            ))}
          </CheckboxGroup>
        </FilterField>
      </FiltersSection>
    </Stack>
  );
});

FiltersContent.displayName = 'FiltersContent';
